// src/services/api.js
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

// แนบ token ทุก request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('wadphol_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// จัดการ 401
api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('wadphol_token');
      localStorage.removeItem('wadphol_user');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

export default api;

// Auth
export const login = (data) => api.post('/auth/login.php', data);
export const getMe = () => api.get('/auth/me.php');

// Users
export const getUsers = (params) => api.get('/auth/users.php', { params });
export const createUser = (data) => api.post('/auth/users.php', data);
export const updateUser = (id, data) => api.put(`/auth/users.php?id=${id}`, data);
export const deleteUser = (id) => api.delete(`/auth/users.php?id=${id}`);

// Meta
export const getLevels = () => api.get('/subjects/groups.php?type=levels');
export const getGroups = () => api.get('/subjects/groups.php?type=groups');
export const getYears = () => api.get('/subjects/groups.php?type=years');
export const getCurrentYear = () => api.get('/subjects/groups.php?type=current_year');

// Subjects
export const getSubjects = (params) => api.get('/subjects/index.php', { params });
export const createSubject      = (data)  => api.post('/subjects/index.php', data);
export const batchCreateSubjects = (items) => api.post('/subjects/batch.php', { items });
export const updateSubject = (id, data) => api.put(`/subjects/index.php?id=${id}`, data);
export const reorderSubjects = (items) => api.post('/subjects/index.php?action=reorder', { items });
export const deleteSubject = (id) => api.delete(`/subjects/index.php?id=${id}`);
export const assignTeachersToSubjects = (data) => api.post('/subjects/assign_teachers.php', data);

// Classrooms
export const getClassrooms = (params) => api.get('/classes/index.php', { params });
export const getStudentSummary = (yearId) => api.get('/classes/index.php', { params: { academic_year_id: yearId, type: 'summary' } });
export const getStudentSummaryDetail = (yearId) => api.get('/classes/index.php', { params: { academic_year_id: yearId, type: 'summary_detail' } });
export const createClassroom = (data) => api.post('/classes/index.php', data);
export const updateClassroom = (id, data) => api.put(`/classes/index.php?id=${id}`, data);
export const deleteClassroom = (id) => api.delete(`/classes/index.php?id=${id}`);

// School Settings (ตั้งค่าโรงเรียน)
export const getSchoolSettings = () => api.get('/school/settings.php');
export const updateSchoolSettings = (data) => api.put('/school/settings.php', data);
export const resetData = (type) => api.delete('/school/reset.php', { data: { type } });
export const uploadSchoolLogo = (formData) => api.post('/school/upload_logo.php', formData, {
  headers: { 'Content-Type': 'multipart/form-data' }
});
export const deleteSchoolLogo = () => api.delete('/school/upload_logo.php');

export const uploadObecLogo = (formData) => api.post('/school/upload_obec_logo.php', formData, {
  headers: { 'Content-Type': 'multipart/form-data' }
});
export const deleteObecLogo = () => api.delete('/school/upload_obec_logo.php');

// Quality Items (คุณลักษณะ / อ่านคิด / สมรรถนะ / IS)
export const getQualityItems    = (params) => api.get('/school/quality_items.php', { params });
export const createQualityItem  = (data)   => api.post('/school/quality_items.php', data);
export const updateQualityItem  = (id, data) => api.put(`/school/quality_items.php?id=${id}`, data);
export const deleteQualityItem  = (id)     => api.delete(`/school/quality_items.php?id=${id}`);
export const reorderQualityItems = (items) => api.patch('/school/quality_items.php', { items });

// Quality Types (หมวด — เปิด/ปิด/เพิ่มหมวด)
export const getQualityTypes    = (params) => api.get('/school/quality_types.php', { params });
export const createQualityType  = (data)   => api.post('/school/quality_types.php', data);
export const updateQualityType  = (id, data) => api.put(`/school/quality_types.php?id=${id}`, data);
export const deleteQualityType  = (id)     => api.delete(`/school/quality_types.php?id=${id}`);

// Quality Scores (คะแนน คุณลักษณะ / อ่านคิด / สมรรถนะ / IS)
export const getQualityInit         = (params) => api.get('/scores/quality_init.php',        { params }); // super-loader
export const getScoreClassroomsInit = ()       => api.get('/scores/classrooms_init.php');          // super-loader ScoreClassroomPage
export const getSubjectResult        = (params) => api.get('/scores/subject_result.php', { params }); // ผลสอบรายวิชา
export const getQualityScores  = (params) => api.get('/scores/quality_scores.php', { params });
export const saveQualityScores = (data)   => api.post('/scores/quality_scores.php', data);
export const getQualityProgress = (params) => api.get('/scores/quality_progress.php', { params });
export const getQualityReport  = (params) => api.get('/scores/quality_report.php', { params });
export const getClassroomReport = (params) => api.get('/scores/classroom_report.php', { params });
export const getEvaluationReport = (params) => api.get('/scores/evaluation_report.php', { params });

// Classroom Settings (ตั้งค่าจำนวนห้อง)
export const getClassroomSettings = (params) => api.get('/classes/settings.php', { params });
export const saveClassroomSettings = (data) => api.post('/classes/settings.php', data);
export const updateClassroomSettings = (id, data) => api.put(`/classes/settings.php?id=${id}`, data);
export const syncClassroomSettings = (year_id) => api.patch(`/classes/settings.php?academic_year_id=${year_id}`);

// Students
export const getStudents = (params) => api.get('/students/index.php', { params });
export const createStudent = (data) => api.post('/students/index.php', data);
export const batchCreateStudents = (data) => api.post('/students/batch.php', data);
export const updateStudent = (id, data) => api.put(`/students/index.php?id=${id}`, data);
export const deleteStudent = (id, params) => api.delete(`/students/index.php?id=${id}`, { params });

// Learning Units
export const getUnits = (params) => api.get('/units/index.php', { params });
export const createUnit = (data) => api.post('/units/index.php', data);
export const updateUnit = (id, data) => api.put(`/units/index.php?id=${id}`, data);
export const deleteUnit = (id) => api.delete(`/units/index.php?id=${id}`);

// Strands
export const getStrands = (subject_id) => api.get(`/indicators/strands.php?subject_id=${subject_id}`);
export const createStrand = (data) => api.post('/indicators/strands.php', data);
export const updateStrand = (id, data) => api.put(`/indicators/strands.php?id=${id}`, data);
export const deleteStrand = (id) => api.delete(`/indicators/strands.php?id=${id}`);

// Indicators
export const getIndicators = (params) => api.get('/indicators/index.php', { params });
export const createIndicator       = (data)  => api.post('/indicators/index.php', data);
export const batchCreateIndicators = (items) => api.post('/indicators/batch.php', { items });
export const batchImportIndicators = (data)  => api.post('/indicators/batch_import.php', data);
export const updateIndicator = (id, data) => api.put(`/indicators/index.php?id=${id}`, data);
export const deleteIndicator = (id) => api.delete(`/indicators/index.php?id=${id}`);
export const copyIndicators = (data) => api.post('/indicators/index.php?action=copy', data);

// Scores
export const getScoreGrid = (params) => api.get('/scores/index.php', { params });
export const saveScores = (data) => api.post('/scores/index.php', data);
export const saveUnitScores = (data) => api.post('/scores/index.php', data); // sub_unit mode: { unit_scores: [...] }
export const getActivityResults = (params) => api.get('/scores/activity_results.php', { params });
export const getClubSummary = (params) => api.get('/scores/club_summary.php', { params });
export const getFinalScores = (params) => api.get('/scores/final.php', { params });
export const saveFinalScores = (data) => api.post('/scores/final.php', data);

// Teachers (ครูและบุคลากรทางการศึกษา)
export const getTeachers = (params) => api.get('/teachers/index.php', { params });
export const getTeacher = (id) => api.get(`/teachers/index.php?id=${id}`);
export const createTeacher = (data) => api.post('/teachers/index.php', data);
export const updateTeacher = (id, data) => api.put(`/teachers/index.php?id=${id}`, data);
export const reorderTeachers = (order) => api.patch('/teachers/index.php', { order });
export const deleteTeacher = (id) => api.delete(`/teachers/index.php?id=${id}`);
export const uploadTeacherPhoto = (formData) => api.post('/teachers/upload_photo.php', formData, {
  headers: { 'Content-Type': 'multipart/form-data' }
});

// Clubs (ชุมนุม)
export const getClubs          = (params) => api.get('/clubs/index.php', { params });
export const getClub           = (id) => api.get(`/clubs/index.php?id=${id}`);
export const createClub        = (data) => api.post('/clubs/index.php', data);
export const updateClub        = (id, data) => api.put(`/clubs/index.php?id=${id}`, data);
export const deleteClub        = (id) => api.delete(`/clubs/index.php?id=${id}`);

// Club Objectives (จุดประสงค์ชุมนุม)
export const getClubObjectives     = (params) => api.get('/clubs/objectives.php', { params });
export const saveClubObjectives    = (data) => api.post('/clubs/objectives.php', data);
export const updateClubObjective   = (id, data) => api.put(`/clubs/objectives.php?id=${id}`, data);
export const deleteClubObjective   = (id) => api.delete(`/clubs/objectives.php?id=${id}`);

// Club Members (สมาชิกชุมนุม)
export const getClubMembers    = (params) => api.get('/clubs/members.php', { params });
export const checkStudentClubs = (params) => api.get('/clubs/members.php', { params: { ...params, type: 'check_clubs' } });
export const addClubMembers    = (data) => api.post('/clubs/members.php', data);
export const removeClubMember  = (id) => api.delete(`/clubs/members.php?id=${id}`);

// Club Scores (คะแนนชุมนุม)
export const getClubScores     = (params) => api.get('/clubs/scores.php', { params });
export const saveClubScores    = (data) => api.post('/clubs/scores.php', data);

// ─── ระบบเช็คชื่อรายคาบ (Attendance) ───
// คาบเรียน
export const getPeriods       = (params) => api.get('/attendance/periods.php', { params });
export const createPeriod     = (data) => api.post('/attendance/periods.php', data);
export const updatePeriod     = (id, data) => api.put(`/attendance/periods.php?id=${id}`, data);
export const deletePeriod     = (id) => api.delete(`/attendance/periods.php?id=${id}`);

// ตารางสอน
export const getSchedules     = (params) => api.get('/attendance/schedules.php', { params });
export const saveSchedules    = (data) => api.post('/attendance/schedules.php', data);
export const deleteSchedules  = (params) => api.delete('/attendance/schedules.php', { params });
export const importSchedules  = (data) => api.post('/attendance/schedule_import.php', data);

// วันหยุด / วันสอนชดเชย
export const getHolidays      = (params) => api.get('/attendance/holidays.php', { params });
export const createHoliday    = (data) => api.post('/attendance/holidays.php', data);
export const updateHoliday    = (id, data) => api.put(`/attendance/holidays.php?id=${id}`, data);
export const deleteHoliday    = (id) => api.delete(`/attendance/holidays.php?id=${id}`);

// เช็คชื่อ
export const getAttendance    = (params) => api.get('/attendance/index.php', { params });
export const saveAttendance   = (data) => api.post('/attendance/index.php', data);
export const getAttendanceReport = (params) => api.get('/attendance/report.php', { params });
